import type { GridApi } from 'ag-grid-community';
import { createContext, useContext, type ReactNode } from 'react';
import type { EntityAdminClass } from '../EntityAdminClass';
import type { EntityAdminRecord } from '../types';

const EntityAdminDrawerContext = createContext<boolean | undefined>(undefined);

export function EntityAdminDrawerProvider({ children }: { children: ReactNode }) {
  return <EntityAdminDrawerContext.Provider value={true}>{children}</EntityAdminDrawerContext.Provider>;
}

/** Returns true if we're inside an EntityAdminDrawer component. */
export function useIsInsideEntityAdminDrawer(): boolean {
  const context = useContext(EntityAdminDrawerContext);
  return context != null;
}

/**
 * Data exposed by EntityAdmin to descendants rendered inside its drawer (e.g. SymbolSelectorContent).
 * Lets specialized drawer content read EntityAdmin-level information (grid data, keyed fields, the
 * parent when adding a child) without pushing that concern up into useEntityAdmin itself.
 */
export interface EntityAdminDrawerData<TRecord extends EntityAdminRecord = EntityAdminRecord> {
  gridApi: GridApi<EntityAdminClass<TRecord>>;
  entityIDField: string | undefined;
  childIDField: string | undefined;
  addingChildEntity: boolean;
  /** The parent entity when adding a child entity, otherwise undefined. */
  parentEntity: EntityAdminClass<TRecord> | undefined;
}

const EntityAdminDrawerDataContext = createContext<EntityAdminDrawerData | undefined>(undefined);

export function EntityAdminDrawerDataProvider<TRecord extends EntityAdminRecord>({
  value,
  children,
}: {
  value: EntityAdminDrawerData<TRecord>;
  children: ReactNode;
}) {
  return (
    <EntityAdminDrawerDataContext.Provider value={value as EntityAdminDrawerData}>
      {children}
    </EntityAdminDrawerDataContext.Provider>
  );
}

export function useEntityAdminDrawerData<TRecord extends EntityAdminRecord = EntityAdminRecord>():
  | EntityAdminDrawerData<TRecord>
  | undefined {
  return useContext(EntityAdminDrawerDataContext) as EntityAdminDrawerData<TRecord> | undefined;
}
